import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { concatClassnames } from "shared/utils/concatClassnames";
import Button, { ButtonArrowTypes, ButtonTypes } from "../button";
import Icon, { IconTypes } from "../icon";

export enum ModalType {
  ROUND,
  SQUARE,
}

const MODAL_TYPE_RECORD: Record<ModalType, string> = {
  [ModalType.ROUND]: "",
  [ModalType.SQUARE]: "gg-py-7 gg-px-8",
};

export interface ModalProps {
  children: any;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  type?: ModalType;
  className?: string;
  showCloseModalButton?: boolean;
  hideHeader?: boolean;
  hasProductFooter?: boolean;
  businessModal?: boolean;
}
const Modal = ({
  children,
  isOpen,
  className,
  setIsOpen,
  type = ModalType.ROUND,
  showCloseModalButton = false,
  hideHeader = false,
  hasProductFooter = false,
  businessModal = false,
}: ModalProps) => {
  const goToCheckout = () => {
    const url = businessModal
      ? "/zakelijk/winkelwagen"
      : // is not business modal
        "/winkelwagen";
    window.location.href = url;
  };

  const showCloseButtonContainer = type === ModalType.SQUARE && !hideHeader;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        className={"gg gg-relative gg-z-50"}
        open={isOpen}
        onClose={() => setIsOpen(false)}
      >
        <div>
          <Transition.Child
            as={Fragment}
            enter="gg-ease-out gg-duration-300"
            enterFrom="gg-opacity-0"
            enterTo="gg-opacity-100"
            leave="gg-ease-in gg-duration-200"
            leaveFrom="gg-opacity-100"
            leaveTo="gg-opacity-0"
          >
            <div className="gg-fixed gg-inset-0 gg-bg-dark-blue-700 gg-transition-opacity" />
          </Transition.Child>

          <div className="gg-fixed gg-inset-0 gg-z-10 gg-overflow-y-auto">
            <div className="gg-flex gg-min-h-full gg-items-center gg-justify-center gg-text-center sm:gg-items-center">
              <Transition.Child
                as={Fragment}
                enter="gg-ease-out gg-duration-300"
                enterFrom="gg-opacity-0 gg-translate-y-4 gg-sm:gg-translate-y-0 gg-sm:gg-scale-95"
                enterTo="gg-opacity-100 gg-translate-y-0 gg-sm:gg-scale-100"
                leave="gg-ease-in gg-duration-200"
                leaveFrom="gg-opacity-100 gg-translate-y-0 gg-sm:gg-scale-100"
                leaveTo="gg-opacity-0 gg-translate-y-4 gg-sm:gg-translate-y-0 gg-sm:gg-scale-95"
              >
                <Dialog.Panel className={"gg-max-w-xl gg-shadow-xl"}>
                  {showCloseButtonContainer && (
                    <div
                      className={concatClassnames(
                        "gg-w-full gg-bg-grey-600 gg-px-8 gg-py-4 gg-text-left",
                      )}
                    >
                      <button
                        className={concatClassnames(
                          "gg-border-0 gg-bg-inherit gg-text-base gg-text-purple-300",
                          "gg-flex gg-cursor-pointer gg-items-center gg-p-0 hover:gg-underline hover:gg-underline-offset-4",
                        )}
                        onClick={() => setIsOpen(false)}
                        data-testid="close-modal-button"
                      >
                        <Icon
                          name={IconTypes.ARROW_RIGHT}
                          className={concatClassnames(
                            "gg-mr-2.5 gg-rotate-180",
                          )}
                        />
                        Terug naar samenstellen
                      </button>
                    </div>
                  )}
                  <div
                    className={concatClassnames(
                      "gg-relative gg-w-full gg-transform gg-overflow-hidden gg-bg-white gg-text-left gg-transition-all",
                      MODAL_TYPE_RECORD[type],
                      className,
                    )}
                  >
                    {showCloseModalButton && (
                      <button
                        className="gg-absolute gg-right-5 gg-top-3 sm:gg-hidden"
                        onClick={() => setIsOpen(false)}
                      >
                        <svg
                          className="text-white gg-h-4 gg-w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="5"
                            stroke="black"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}

                    {children}
                  </div>
                  {hasProductFooter && (
                    <div className="w-full gg-sticky gg-bottom-0 gg-border-t gg-bg-white gg-py-7 md:gg-static">
                      <div className="gg-flex gg-justify-between gg-px-4">
                        <Button
                          type={ButtonTypes.UNSTYLED}
                          arrow={ButtonArrowTypes.LEFT}
                          onClick={() => setIsOpen(false)}
                          className="gg-text-gray-500 hover:gg-text-gray-800"
                          data-testid="close-modal-button"
                        >
                          Verder winkelen
                        </Button>
                        <Button
                          type={ButtonTypes.YELLOW}
                          arrow={ButtonArrowTypes.RIGHT}
                          onClick={goToCheckout}
                        >
                          Bestellen
                        </Button>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
export default Modal;
